// Localização: ./src/components/Pacientes/PacienteFormContent.jsx (Versão 3.2.0 - Melhorias em validações e mensagens de erro)

import React, { useState, useEffect } from 'react';
import { usePacientes, ERROR_TYPES } from '../../hooks/usePacientes'; 
import Modal from '../UI/Modal';
import { getPacienteFotoUrl } from '../../utils/imageUtils';
import ErrorMessage from '../Common/ErrorMessage';
import {
    validateNomeCompleto,
    validateCPF,
    validateCEP,
    validateTelefone,
    validateDataNascimento,
    validateEstado,
    validateFotoFile,
    formatCPF,
    formatCEP,
    formatTelefone
} from '../../utils/validationUtils'; 

// Componente Principal do Formulário
function PacienteFormContent({ isOpen, onClose, pacienteData }) {
    const isEditing = !!pacienteData;
    // Puxa as funções necessárias do hook, incluindo o setError
    const { savePaciente, refreshing, error, setError } = usePacientes(); 

    const [formData, setFormData] = useState({
        // Nome da chave alinhado com o backend
        nome_completo: '',
        data_nascimento: '',
        rg: '',
        cpf: '',
        nit_pis: '',
        cartao_sus: '',
        numero_beneficio: '',
        endereco: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: '',
        telefone_residencial: '',
        celular: '',
        condicao_medica: '',
        status: 'Ativo',
    });
    const [fotoFile, setFotoFile] = useState(null); 
    const [fotoPreview, setFotoPreview] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({}); // Erros de validação por campo 

    // Efeito para carregar dados do paciente quando em modo de edição
    useEffect(() => {
        // Limpeza inicial do erro
        setError(null); 

        if (isEditing && pacienteData) {
            
            const loadedData = {};
            
            // Tentativa de carregar os dados
            // Priorizamos nomes_com_underline, mas mantemos o fallback
            loadedData.nome_completo = pacienteData.nome_completo || pacienteData.NomeCompleto || '';
            loadedData.data_nascimento = pacienteData.data_nascimento || pacienteData.DataNascimento;
            loadedData.rg = pacienteData.rg || '';
            loadedData.cpf = pacienteData.cpf || '';
            loadedData.nit_pis = pacienteData.nit_pis || pacienteData.nitPis || '';
            loadedData.cartao_sus = pacienteData.cartao_sus || pacienteData.cartaoSus || '';
            loadedData.numero_beneficio = pacienteData.numero_beneficio || pacienteData.numeroBeneficio || '';
            loadedData.endereco = pacienteData.endereco || pacienteData.Endereco || '';
            loadedData.bairro = pacienteData.bairro || '';
            loadedData.cidade = pacienteData.cidade || '';
            loadedData.estado = pacienteData.estado || '';
            loadedData.cep = pacienteData.cep || '';
            loadedData.telefone_residencial = pacienteData.telefone_residencial || pacienteData.telefoneResidencial || '';
            loadedData.celular = pacienteData.celular || '';
            // Assumindo que 'condicao_medica' pode vir como 'HistoricoMedico' do DB
            loadedData.condicao_medica = pacienteData.condicao_medica || pacienteData.HistoricoMedico || ''; 
            loadedData.status = pacienteData.status || 'Ativo';
            
            // Configura o formData
            setFormData({
                ...loadedData,
                // Formata a data para o padrão 'yyyy-mm-dd' do input[type=date]
                data_nascimento: loadedData.data_nascimento ? String(loadedData.data_nascimento).split('T')[0] : '', 
            });

            // Configura o preview da foto
            // 🛑 CORREÇÃO DE CONSISTÊNCIA: Priorizamos a chave fotoURL (camel case)
            const fotoUrlFromApi = pacienteData.fotoURL || pacienteData.FotoURL || pacienteData.fotourl;
            
            if (fotoUrlFromApi) {
                // 🛑 CENTRALIZAÇÃO DE LÓGICA: Usa o utilitário corrigido para gerar a URL
                setFotoPreview(getPacienteFotoUrl(fotoUrlFromApi));
            } else {
                setFotoPreview(null);
            }

            setFotoFile(null); // Garante que nenhum novo arquivo está pendente de upload no início da edição
            
        } else {
            // Limpa o formulário para a criação
            setFormData({
                nome_completo: '',
                data_nascimento: '',
                rg: '',
                cpf: '',
                nit_pis: '',
                cartao_sus: '',
                numero_beneficio: '',
                endereco: '',
                bairro: '',
                cidade: '',
                estado: '',
                cep: '',
                telefone_residencial: '',
                celular: '',
                condicao_medica: '',
                status: 'Ativo',
            });
            setFotoFile(null);
            setFotoPreview(null);
        }
    }, [pacienteData, isEditing, setError]); // Dependências do useEffect

    // Handler de mudança de campos de texto com formatação automática
    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;
        
        // Formatação automática baseada no campo
        if (name === 'cpf') {
            processedValue = formatCPF(value);
        } else if (name === 'cep') {
            processedValue = formatCEP(value);
        } else if (name === 'telefone_residencial' || name === 'celular') {
            processedValue = formatTelefone(value);
        } else if (name === 'estado') {
            processedValue = value.toUpperCase().slice(0, 2);
        }
        
        setFormData(prev => ({ ...prev, [name]: processedValue }));
        
        // Limpa erro do campo quando o usuário começa a digitar
        if (fieldErrors[name]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };
    
    // Handler de mudança de arquivo de foto com validação
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        
        if (file) {
            const validation = validateFotoFile(file);
            if (!validation.valid) {
                setError({
                    type: ERROR_TYPES.VALIDATION,
                    message: validation.message,
                    details: []
                });
                e.target.value = ''; // Limpa o input
                return;
            }
        }
        
        setFotoFile(file);

        if (file) {
            // Cria um preview temporário para o arquivo local
            setFotoPreview(URL.createObjectURL(file));
        } else {
            // 🛑 CORRIGIDO: Usa a função utilitária para manter a foto antiga (se existir)
            const oldFotoUrl = pacienteData?.fotoURL || pacienteData?.FotoURL || pacienteData?.fotourl;
            setFotoPreview(isEditing && oldFotoUrl ? getPacienteFotoUrl(oldFotoUrl) : null);
        }
    };

    // Função auxiliar para obter o label do campo
    const getFieldLabel = (fieldName) => {
        const labels = {
            nome_completo: 'Nome Completo',
            cpf: 'CPF',
            cep: 'CEP',
            telefone_residencial: 'Telefone Residencial',
            celular: 'Celular',
            data_nascimento: 'Data de Nascimento',
            estado: 'Estado'
        };
        return labels[fieldName] || fieldName;
    };

    // Função para fechar o modal e limpar o erro
    const handleClose = () => {
        setError(null);
        setFieldErrors({});
        onClose();
    };

    // Função de Submissão com validações completas
    const handleSubmit = async () => {
        setError(null);
        setFieldErrors({});
        
        const errors = {};
        const validationDetails = [];
        
        // 1. Validação de Nome Completo (obrigatório)
        const nomeValidation = validateNomeCompleto(formData.nome_completo);
        if (!nomeValidation.valid) {
            errors.nome_completo = nomeValidation.message;
            validationDetails.push(nomeValidation.message);
        }
        
        // 2. Validação de CPF (obrigatório)
        const cpfValidation = validateCPF(formData.cpf, true); // true = obrigatório
        if (!cpfValidation.valid) {
            errors.cpf = cpfValidation.message;
            validationDetails.push(`CPF: ${cpfValidation.message}`);
        }
        
        // 3. Validação de CEP (opcional, mas se preenchido deve ser válido)
        if (formData.cep) {
            const cepValidation = validateCEP(formData.cep);
            if (!cepValidation.valid) {
                errors.cep = cepValidation.message;
                validationDetails.push(`CEP: ${cepValidation.message}`);
            }
        }
        
        // 4. Validação de Telefones (opcionais, mas se preenchidos devem ser válidos)
        if (formData.telefone_residencial) {
            const telValidation = validateTelefone(formData.telefone_residencial);
            if (!telValidation.valid) {
                errors.telefone_residencial = telValidation.message;
                validationDetails.push(`Telefone Residencial: ${telValidation.message}`);
            }
        }
        
        if (formData.celular) {
            const celValidation = validateTelefone(formData.celular);
            if (!celValidation.valid) {
                errors.celular = celValidation.message;
                validationDetails.push(`Celular: ${celValidation.message}`);
            }
        }
        
        // 5. Validação de Data de Nascimento (opcional, mas se preenchida deve ser válida)
        if (formData.data_nascimento) {
            const dataValidation = validateDataNascimento(formData.data_nascimento);
            if (!dataValidation.valid) {
                errors.data_nascimento = dataValidation.message;
                validationDetails.push(`Data de Nascimento: ${dataValidation.message}`);
            }
        }
        
        // 6. Validação de Estado (opcional, mas se preenchido deve ser válido)
        if (formData.estado) {
            const estadoValidation = validateEstado(formData.estado);
            if (!estadoValidation.valid) {
                errors.estado = estadoValidation.message;
                validationDetails.push(`Estado: ${estadoValidation.message}`);
            }
        }
        
        // Se houver erros de validação, exibe e interrompe
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setError({
                type: ERROR_TYPES.VALIDATION,
                message: 'Por favor, corrija os erros abaixo antes de continuar.',
                details: validationDetails
            });
            return;
        }

        const formPayload = new FormData();

        // 2. Adicionar campos de texto (convertendo strings vazias para null para campos opcionais)
        Object.entries(formData).forEach(([key, value]) => {
            // Para campos opcionais, converte string vazia para null
            // Campos obrigatórios: nome_completo
            const isOptional = key !== 'nome_completo';
            let processedValue = String(value).trim();
            
            // Se for campo opcional e estiver vazio, envia como string vazia (será convertido no backend)
            // Mas para data_nascimento, envia string vazia que será tratada no backend
            if (isOptional && processedValue === '') {
                processedValue = '';
            }
            
            formPayload.append(key, processedValue);
        });

        // 3. Adicionar ID (se estiver editando)
        if (isEditing) {
            formPayload.append('id', pacienteData.id);
        }

        // 4. Adicionar o arquivo de foto
        if (fotoFile) {
            // A chave 'foto' deve bater com o que o Multer espera no backend
            formPayload.append('foto', fotoFile); 
        }
        
        // 5. Chamada ao hook de salvamento
        const success = await savePaciente(formPayload, isEditing);

        if (success) {
            handleClose(); 
        }
    };
    
    return (
        <Modal 
            isOpen={isOpen} 
            onClose={handleClose} 
            title={isEditing ? `Editar Paciente: ${pacienteData?.nome_completo || '...'}` : 'Cadastrar Novo Paciente'}
            size="2xl"
        >
            <div className="space-y-6"> 
                
                {/* Exibição de Erros Melhorada */}
                {error && (
                    <div className="space-y-3">
                        <ErrorMessage
                            message={typeof error === 'string' ? error : error.message}
                            type={typeof error === 'object' ? error.type : ERROR_TYPES.GENERIC}
                            details={typeof error === 'object' ? error.details : []}
                            onClose={() => {
                                setError(null);
                                setFieldErrors({});
                            }}
                        />
                        {/* Lista de campos com erro */}
                        {Object.keys(fieldErrors).length > 0 && (
                            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 shadow-sm">
                                <p className="text-sm font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                                    <span className="text-yellow-600">⚠️</span>
                                    Campos que precisam de correção:
                                </p>
                                <ul className="list-disc list-inside space-y-1.5 text-sm text-yellow-700">
                                    {Object.entries(fieldErrors).map(([field, message]) => (
                                        <li key={field} className="flex items-start gap-2">
                                            <span className="font-semibold text-yellow-800">{getFieldLabel(field)}:</span>
                                            <span>{message}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
                

                {/* Seção: IDENTIFICAÇÃO DO PACIENTE */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-5 py-3 border-b border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-700 text-center">IDENTIFICAÇÃO DO PACIENTE</h3>
                    </div>
                    <div className="p-5 space-y-5">
                        {/* Linha 1: Paciente e RG */}
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                            <div className="sm:col-span-10">
                                <label htmlFor="nome_completo" className="block text-sm font-medium text-gray-700 mb-2">Paciente: <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="nome_completo" 
                                    id="nome_completo"
                                    required
                                    value={formData.nome_completo}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:outline-none focus:ring-2 sm:text-sm transition-all shadow-sm ${
                                        fieldErrors.nome_completo 
                                            ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500' 
                                            : 'border-gray-300 focus:ring-emerald-500/20 focus:border-emerald-500'
                                    }`}
                                />
                                {fieldErrors.nome_completo && (
                                    <p className="mt-1 text-xs text-red-600">{fieldErrors.nome_completo}</p>
                                )}
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="rg" className="block text-sm font-medium text-gray-700 mb-2">RG:</label>
                                <input
                                    type="text"
                                    name="rg"
                                    id="rg"
                                    value={formData.rg}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 sm:text-sm transition-all shadow-sm"
                                />
                            </div>
                        </div>

                        {/* Linha 2: CPF, NIT/PIS, Cartão SUS */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <label htmlFor="cpf" className="block text-sm font-medium text-gray-700 mb-2">CPF: <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="cpf"
                                    id="cpf"
                                    value={formData.cpf}
                                    onChange={handleChange}
                                    placeholder="000.000.000-00"
                                    maxLength={14}
                                    required
                                    className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:outline-none focus:ring-2 sm:text-sm transition-all shadow-sm ${
                                        fieldErrors.cpf 
                                            ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500' 
                                            : 'border-gray-300 focus:ring-emerald-500/20 focus:border-emerald-500'
                                    }`}
                                />
                                {fieldErrors.cpf && (
                                    <p className="mt-1 text-xs text-red-600">{fieldErrors.cpf}</p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="nit_pis" className="block text-sm font-medium text-gray-700 mb-2">NIT/PIS:</label>
                                <input
                                    type="text"
                                    name="nit_pis"
                                    id="nit_pis"
                                    value={formData.nit_pis}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 sm:text-sm transition-all shadow-sm"
                                />
                            </div>
                            <div>
                                <label htmlFor="cartao_sus" className="block text-sm font-medium text-gray-700 mb-2">Cartão SUS:</label>
                                <input
                                    type="text"
                                    name="cartao_sus"
                                    id="cartao_sus"
                                    value={formData.cartao_sus}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 sm:text-sm transition-all shadow-sm"
                                />
                            </div>
                        </div>

                        {/* Linha 3: NB */}
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                            <div className="sm:col-span-2">
                                <label htmlFor="numero_beneficio" className="block text-sm font-medium text-gray-700 mb-2">NB:</label>
                                <input
                                    type="text"
                                    name="numero_beneficio"
                                    id="numero_beneficio"
                                    value={formData.numero_beneficio}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 sm:text-sm transition-all shadow-sm"
                                />
                            </div>
                        </div>

                        {/* Linha 4: Endereço Residencial e Bairro */}
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                            <div className="sm:col-span-10">
                                <label htmlFor="endereco" className="block text-sm font-medium text-gray-700 mb-2">Endereço Residencial:</label>
                                <input
                                    type="text"
                                    name="endereco"
                                    id="endereco"
                                    value={formData.endereco}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 sm:text-sm transition-all shadow-sm"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="bairro" className="block text-sm font-medium text-gray-700 mb-2">Bairro:</label>
                                <input
                                    type="text"
                                    name="bairro"
                                    id="bairro"
                                    value={formData.bairro}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 sm:text-sm transition-all shadow-sm"
                                />
                            </div>
                        </div>

                        {/* Linha 5: Cidade, Estado, CEP */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <label htmlFor="cidade" className="block text-sm font-medium text-gray-700 mb-2">Cidade:</label>
                                <input
                                    type="text"
                                    name="cidade"
                                    id="cidade"
                                    value={formData.cidade}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 sm:text-sm transition-all shadow-sm"
                                />
                            </div>
                            <div>
                                <label htmlFor="estado" className="block text-sm font-medium text-gray-700 mb-2">Estado:</label>
                                <input
                                    type="text"
                                    name="estado"
                                    id="estado"
                                    value={formData.estado}
                                    onChange={handleChange}
                                    maxLength={2}
                                    placeholder="UF"
                                    className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:outline-none focus:ring-2 sm:text-sm transition-all shadow-sm uppercase ${
                                        fieldErrors.estado 
                                            ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500' 
                                            : 'border-gray-300 focus:ring-emerald-500/20 focus:border-emerald-500'
                                    }`}
                                />
                                {fieldErrors.estado && (
                                    <p className="mt-1 text-xs text-red-600">{fieldErrors.estado}</p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="cep" className="block text-sm font-medium text-gray-700 mb-2">CEP:</label>
                                <input
                                    type="text"
                                    name="cep"
                                    id="cep"
                                    value={formData.cep}
                                    onChange={handleChange}
                                    placeholder="00000-000"
                                    maxLength={9}
                                    className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:outline-none focus:ring-2 sm:text-sm transition-all shadow-sm ${
                                        fieldErrors.cep 
                                            ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500' 
                                            : 'border-gray-300 focus:ring-emerald-500/20 focus:border-emerald-500'
                                    }`}
                                />
                                {fieldErrors.cep && (
                                    <p className="mt-1 text-xs text-red-600">{fieldErrors.cep}</p>
                                )}
                            </div>
                        </div>

                        {/* Linha 6: Telefone Residencial e Celular */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="telefone_residencial" className="block text-sm font-medium text-gray-700 mb-2">Telefone Residencial:</label>
                                <input
                                    type="text"
                                    name="telefone_residencial"
                                    id="telefone_residencial"
                                    value={formData.telefone_residencial}
                                    onChange={handleChange}
                                    placeholder="(00) 0000-0000"
                                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 sm:text-sm transition-all shadow-sm"
                                />
                            </div>
                            <div>
                                <label htmlFor="celular" className="block text-sm font-medium text-gray-700 mb-2">Celular:</label>
                                <input
                                    type="text"
                                    name="celular"
                                    id="celular"
                                    value={formData.celular}
                                    onChange={handleChange}
                                    placeholder="(00) 00000-0000"
                                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 sm:text-sm transition-all shadow-sm"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Seção de Dados Adicionais */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <label htmlFor="data_nascimento" className="block text-sm font-medium text-gray-700 mb-2">Data de Nascimento</label>
                            <input
                                type="date"
                                name="data_nascimento"
                                id="data_nascimento"
                                value={formData.data_nascimento}
                                onChange={handleChange}
                                className={`w-full px-4 py-2.5 bg-white border rounded-xl shadow-sm focus:outline-none focus:ring-2 sm:text-sm transition-all ${
                                    fieldErrors.data_nascimento 
                                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500' 
                                        : 'border-gray-300 focus:ring-emerald-500/20 focus:border-emerald-500'
                                }`}
                            />
                            {fieldErrors.data_nascimento && (
                                <p className="mt-1 text-xs text-red-600">{fieldErrors.data_nascimento}</p>
                            )}
                        </div>
                        
                        {isEditing && (
                            <div>
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                <select
                                    name="status"
                                    id="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 sm:text-sm transition-all"
                                >
                                    <option value="Ativo">Ativo</option>
                                    <option value="Inativo">Inativo</option>
                                </select>
                            </div>
                        )}
                    </div>
                    
                    <div>
                        <label htmlFor="condicao_medica" className="block text-sm font-medium text-gray-700 mb-2">Condição Médica Principal</label>
                        <textarea
                            name="condicao_medica"
                            id="condicao_medica"
                            rows="3"
                            value={formData.condicao_medica}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 sm:text-sm transition-all resize-none"
                        />
                    </div>
                </div>

                {/* Seção de Foto */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 shadow-sm p-5">
                    <h4 className="text-md font-semibold text-gray-800 mb-4">Foto do Paciente</h4>
                    <div className="flex items-center space-x-6">
                        <div className="flex-shrink-0 h-24 w-24 rounded-2xl overflow-hidden bg-gray-100 flex items-center justify-center border border-gray-200 shadow-sm">
                            {fotoPreview ? (
                                <img src={fotoPreview} alt="Preview do Paciente" className="h-full w-full object-cover" />
                            ) : (
                                <svg className="h-12 w-12 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 20.993V24H0v-2.996c0-4.606 5.86-6.91 12-6.91s12 2.304 12 6.91zM12 11c3.31 0 6-2.69 6-6s-2.69-6-6-6-6 2.69-6 6 2.69 6 6 6z" />
                                </svg>
                            )}
                        </div>
                        <div className="flex flex-col gap-3">
                            <label htmlFor="foto" className="cursor-pointer bg-white py-2.5 px-5 border border-emerald-300 rounded-xl shadow-sm text-sm font-medium text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500/20 transition-all">
                                {isEditing && pacienteData?.fotoURL ? 'Mudar Foto' : 'Upload Foto'}
                                <input
                                    type="file"
                                    name="foto"
                                    id="foto"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="sr-only"
                                />
                            </label>
                            {/* 🛑 A verificação para mostrar o botão usa fotoPreview, que é o estado */}
                            {fotoPreview && (
                                <button
                                    type="button"
                                    onClick={() => { 
                                        setFotoFile(null); 
                                        setFotoPreview(null); // Limpar completamente o preview no clique de "Remover"
                                        // Se quisermos uma opção de "reverter para a foto salva", precisaríamos de mais lógica aqui.
                                    }}
                                    className="text-red-600 hover:text-red-800 text-sm font-medium transition-colors px-2 py-1 rounded-lg hover:bg-red-50"
                                >
                                    Remover Arquivo
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Botão de Submissão */}
                <div className="pt-4">
                    <button
                        type="button" 
                        onClick={handleSubmit} 
                        disabled={refreshing}
                        className={`w-full flex justify-center py-3.5 px-6 border border-transparent rounded-xl text-sm font-semibold text-white transition-all duration-200
                            ${refreshing 
                                ? 'bg-emerald-400 cursor-not-allowed shadow-sm' 
                                : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500/30 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'}`}
                    >
                        {refreshing ? 'Salvando...' : (isEditing ? 'Salvar Alterações' : 'Criar Paciente')}
                    </button>
                </div>
            </div> 
        </Modal>
    );
}

export default PacienteFormContent;